import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Send, ChevronDown } from 'lucide-react';
import { useSiteInfo } from '../context/SiteContext';
import { isValidContactPhone } from '../lib/utils';

interface ContactFormProps {
  defaultService?: string;
  title?: string;
  subtitle?: string;
  collapsible?: boolean;
  submitButtonText?: string;
  buttonClassName?: string;
}

export default function ContactForm({
  defaultService = 'Tư vấn chung',
  title = 'NHẬN BÁO GIÁ NHANH',
  subtitle = 'Để lại thông tin, chúng tôi sẽ liên hệ tư vấn và báo giá ngay qua Zalo',
  collapsible = false,
  submitButtonText = 'GỬI YÊU CẦU & MỞ ZALO',
  buttonClassName = 'inline-flex items-center justify-center px-8 py-3 bg-accent text-primary-dark font-bold rounded-md hover:bg-orange-300 transition-colors disabled:opacity-70 cursor-pointer disabled:cursor-not-allowed'
}: ContactFormProps) {
  const [formData, setFormData] = useState({ name: '', phone: '', service: defaultService, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(!collapsible);
  const { siteInfo } = useSiteInfo();

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      alert('Vui lòng nhập tên và số điện thoại!');
      return;
    }
    if (!isValidContactPhone(formData.phone)) {
      alert('Vui lòng kiểm tra lại số điện thoại.');
      return;
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'contacts'), {
        ...formData,
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        status: 'new',
        createdAt: new Date().toISOString()
      });

      const zaloMessage = `Chào ${siteInfo.name},\nTôi là: ${formData.name}\nSĐT: ${formData.phone}\nQuan tâm dịch vụ: ${formData.service}\nGhi chú: ${formData.message}`;

      try {
        await navigator.clipboard.writeText(zaloMessage);
        alert('Đã copy thông tin yêu cầu. Vui lòng dán (Paste) vào khung chat Zalo để gửi cho chúng tôi nhé!');
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }

      const zaloUrl = `https://zalo.me/${siteInfo.phone.replace(/\./g, '').replace(/ /g, '')}`;
      window.open(zaloUrl, '_blank');

      setFormData({ name: '', phone: '', service: defaultService, message: '' });
    } catch (error) {
      console.error('Error submitting contact:', error);
      alert('Có lỗi xảy ra, vui lòng thử lại sau.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-blue-50 rounded-xl border border-blue-100 overflow-hidden">
      {/* Header - luôn hiển thị, click để toggle nếu collapsible */}
      <div
        className="p-6 pb-4 text-center"
      >
        <h2 className="text-2xl font-bold text-primary uppercase inline-flex items-center gap-1">
          {collapsible ? <button type="button" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} className="inline-flex items-center gap-1 cursor-pointer">
          {title}
            <ChevronDown
              size={16}
              className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
            />
          </button> : title}
        </h2>
        <p className="text-center text-gray-600 mt-2 text-sm">{subtitle}</p>
      </div>

      {/* Form - chỉ hiển thị khi isOpen */}
      {isOpen && (
        <div className="px-6 pb-6">
          <form onSubmit={handleContactSubmit} className="space-y-4 max-w-2xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Họ và tên *</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Nhập họ tên"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Số điện thoại (Zalo) *</label>
                <input
                  type="tel"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Nhập số điện thoại"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dịch vụ quan tâm</label>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                value={formData.service}
                onChange={e => setFormData({ ...formData, service: e.target.value })}
              >
                <option value="Tư vấn chung">Tư vấn chung</option>
                <option value="Khoan cọc nhồi">Khoan cọc nhồi</option>
                <option value="Ép cừ U200">Ép cừ U200</option>
                <option value="Chống lún nhà">Chống lún nhà</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú thêm</label>
              <textarea
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
                placeholder="Nhập yêu cầu chi tiết (nếu có)"
                value={formData.message}
                onChange={e => setFormData({ ...formData, message: e.target.value })}
              />
            </div>
            <div className="text-center pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className={buttonClassName}
              >
                {isSubmitting ? 'Đang gửi...' : submitButtonText}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
